import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Box, Grid, Button, Typography, Snackbar, CircularProgress, Tabs, Tab } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ReactQuill, { Quill } from 'react-quill';
import ImageUploader from 'quill-image-uploader';
import 'react-quill/dist/quill.snow.css';
import api from '../../services/api';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`policy-tabpanel-${index}`}
            aria-labelledby={`policy-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function Politicas() {
    const [stateAlert, setStateAlert] = useState({ status: false, type: 'success', msg: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [configId, setConfigId] = useState(null);
    
    // Estados para os editores
    const [privatePolicy, setPrivatePolicy] = useState('');
    const [subscriberPolicy, setSubscriberPolicy] = useState('');
    
    const quillRefPrivate = useRef();
    const quillRefSubscriber = useRef();

    const Alert = React.forwardRef(function Alert(props, ref) {
        return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    });

    const modules = useMemo(() => ({
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', "strike"],
                [{ 'list': 'ordered' }, { 'list': 'bullet' },
                { 'indent': '-1' }, { 'indent': '+1' }],
                ['image', "link"],
                [{ 'color': ['#000000', '#e60000', '#ff9900', '#ffff00', '#008a00', '#0066cc', '#9933ff', '#ffffff', '#facccc', '#ffebcc', '#ffffcc', '#cce8cc', '#cce0f5', '#ebd6ff', '#bbbbbb', '#f06666', '#ffc266', '#ffff66', '#66b966', '#66a3e0', '#c285ff', '#888888', '#a10000', '#b26b00', '#b2b200', '#006100', '#0047b2', '#6b24b2', '#444444', '#5c0000', '#663d00', '#666600', '#003700', '#002966', '#3d1466'] }]
            ],
            imageUploader: {
                upload: (file) => {
                    return new Promise((resolve, reject) => {
                        // Aqui você pode implementar o upload da imagem se necessário
                        resolve('');
                    });
                }
            }
        },
    }), []);

    async function loadPolicies() {
        try {
            setLoading(true);
            const response = await api.get('/configs/1');
            
            if (response.status === 200 && response.data) {
                const config = response.data.data;
                setConfigId(config.id);
                setPrivatePolicy(config.attributes.privatePolicy || '');
                setSubscriberPolicy(config.attributes.subscriberPolicy || '');
            }
        } catch (error) {
            console.error('Erro ao carregar políticas:', error);
            setStateAlert({ 
                status: true, 
                type: 'error', 
                msg: 'Erro ao carregar políticas: ' + error.message 
            });
        } finally {
            setLoading(false);
        }
    }

    async function savePolicies() {
        try {
            setSaving(true);
            
            const data = {
                data: {
                    privatePolicy: privatePolicy,
                    subscriberPolicy: subscriberPolicy
                }
            };

            const response = await api.put(`/configs/1`, data);
            
            if (response.status === 200) {
                setStateAlert({ 
                    status: true, 
                    type: 'success', 
                    msg: 'Políticas salvas com sucesso!' 
                });
            }
        } catch (error) {
            console.error('Erro ao salvar políticas:', error);
            setStateAlert({ 
                status: true, 
                type: 'error', 
                msg: 'Erro ao salvar políticas: ' + error.message 
            });
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        if (quillRefPrivate.current) {
            Quill.register('modules/imageUploader', ImageUploader);
        }
        loadPolicies();

        return () => {
            quillRefPrivate.current = null;
            quillRefSubscriber.current = null;
        };
    }, []);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Box sx={{ width: '100%', minHeight: '100vh' }} p={5}>
            <Grid container px={12} sx={{ justifyContent: 'space-between', mb: 3 }}>
                <Grid item xs={12}>
                    <Typography variant='h4'>Políticas do Sistema</Typography>
                    <Typography variant='body2' color="text.secondary" sx={{ mt: 1 }}>
                        Configure as políticas de privacidade e inscrições do sistema
                    </Typography>
                </Grid>
            </Grid>

            <Snackbar
                open={stateAlert.status}
                autoHideDuration={3000}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setStateAlert({ status: false, type: 'success', msg: '' })}>
                <Alert sx={{ width: '100%' }} severity={stateAlert.type}>
                    {stateAlert.msg}
                </Alert>
            </Snackbar>

            {loading ? (
                <Box sx={{ width: '100%', height: '50vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress size={50} />
                </Box>
            ) : (
                <Grid container px={12}>
                    <Grid item xs={12}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                            <Tabs value={tabValue} onChange={handleTabChange} aria-label="policy tabs">
                                <Tab label="Política de Privacidade" />
                                <Tab label="Política de Inscrições" />
                            </Tabs>
                        </Box>

                        <TabPanel value={tabValue} index={0}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant='h6' sx={{ mb: 2 }}>
                                    Política de Privacidade
                                </Typography>
                                <Typography variant='body2' color="text.secondary" sx={{ mb: 2 }}>
                                    Configure os termos de privacidade que serão exibidos aos usuários
                                </Typography>
                                <ReactQuill
                                    ref={quillRefPrivate}
                                    value={privatePolicy}
                                    style={{ height: "500px", marginBottom: "60px" }}
                                    modules={modules}
                                    onChange={setPrivatePolicy}
                                    theme='snow'
                                />
                            </Box>
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            <Box sx={{ mb: 3 }}>
                                <Typography variant='h6' sx={{ mb: 2 }}>
                                    Política de Inscrições
                                </Typography>
                                <Typography variant='body2' color="text.secondary" sx={{ mb: 2 }}>
                                    Configure as regras e termos para inscrições no sistema
                                </Typography>
                                <ReactQuill
                                    ref={quillRefSubscriber}
                                    value={subscriberPolicy}
                                    style={{ height: "500px", marginBottom: "60px" }}
                                    modules={modules}
                                    onChange={setSubscriberPolicy}
                                    theme='snow'
                                />
                            </Box>
                        </TabPanel>

                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <Button
                                variant="outlined"
                                onClick={loadPolicies}
                                disabled={saving}
                                sx={{ mr: 2 }}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={savePolicies}
                                disabled={saving}>
                                {saving ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                                        Salvando...
                                    </>
                                ) : (
                                    'Salvar Políticas'
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            )}
        </Box>
    );
}
